import { IconCircleCheck, IconTrash, IconXboxX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getApi, imageUploadApi, postApi } from '../../../api/call.api';
import { urlApi } from '../../../api/url.api';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import { loadingHide, loadingShow } from '../../../loadingUi/loading.controller';
import AddHighlightColorModal from '../Component/ChatSettingComponents/AddHighlightColorModal';

const ChatSettings = () => {

    const [settingData, setSettingData] = useState(null);
    const [languageList, setLanguageList] = useState(null);
    const [pdfFile, setPdfFile] = useState(null);
    const [colorList, setColorList] = useState([]);

    const getSetting = async () => {
        loadingShow();
        let resp = await getApi(urlApi.getChatSettingData);
        loadingHide();
        if (resp.responseCode === 200) {
            setSettingData(resp?.data);
            document.getElementById('language-list').value = resp?.data?.chatLanguageId || "";
        } else {
            setSettingData(null);
        }
    }

    const getLanguageList = async () => {
        let resp = await getApi(urlApi.getLanguageList);
        if (resp?.responseCode === 200) {
            setLanguageList(resp?.data);
        } else {
            setLanguageList([])
        }
    }

    useEffect(() => {
        getLanguageList();
        getSetting();
        getColorList();
        return () => { };
    }, []);

    const setAutoResolveTimer = async () => {
        let timer = document.getElementById("auto-chat-resolve-time")?.value?.trim();
        if (settingData?.chatAutoCloseDuration == timer) return;
        loadingShow();
        let postData = {
            "chatAutoCloseDuration": timer
        }
        let resp = await postApi(postData, urlApi.setChatAutoResolveDuration);
        loadingHide();
        if (resp.responseCode === 200) {
            getSetting();
            toast.success(resp?.message);
        } else {
            toast.error(resp?.message);
        }
    }

    const setLang = async (lang) => {
        loadingShow();
        let resp = await getApi(urlApi?.setLanguageForChat + lang);
        loadingHide();
        if (resp?.responseCode === 200) {
            getSetting();
            toast.success(resp?.message);
        } else {
            toast.error(resp?.message)
        }
    }

    const ingestPdf = async () => {
        if (!pdfFile) {
            toast.error("Please Choose a PDF File.")
            return;
        }
        const maxSizeInBytes = 25 * 1024 * 1024; // 25MB
        if (pdfFile?.size > maxSizeInBytes) {
            toast.error("File size exceeds the limit (25MB).");
            return;
        }
        const formData = new FormData();
        formData.append('pdf', pdfFile);
        loadingShow();
        let uploadResp = await imageUploadApi(formData, urlApi.uploadPdf);
        if (uploadResp?.responseCode === 200) {
            let postData = {
                pdfUrl: uploadResp?.data
            }
            let resp = await postApi(postData, urlApi.ingestPdf);
            loadingHide();
            if (resp?.responseCode === 200) {
                toast.success(resp?.message);
                setPdfFile(null);
                document.getElementById('pdf-selector-input').value = ""
            } else {
                toast.error(resp?.message)
            }
        } else {
            loadingHide();
            toast.error(uploadResp?.message);
        }
    }

    const toggleCallFeature = async () => {
        const newStatus = settingData?.isCallEnabled == 1 ? 2 : 1;
        loadingShow();
        const resp = await getApi(urlApi.toggleCallFeature + newStatus);
        loadingHide();
        if (resp.responseCode === 200) {
            toast.success(resp.message);
            getSetting();
        } else {
            toast.error(resp.message);
        }
    }

    const toggleAIFeature = async () => {
        const newStatus = settingData?.isAIEnabled == 1 ? 2 : 1;
        loadingShow();
        const resp = await getApi(urlApi.toggleAIFeature + newStatus);
        loadingHide();
        if (resp.responseCode === 200) {
            toast.success(resp.message);
            getSetting();
        } else {
            toast.error(resp.message);
        }
    }

    const updateAiSwitchMessageCount = async () => {
        let messageCount = document.getElementById("ai-switch-message-count")?.value?.trim();
        if (settingData?.messageCount == messageCount) return;
        loadingShow();
        let resp = await getApi(urlApi.updateAISwitchMessageCount + messageCount);
        loadingHide();
        if (resp.responseCode === 200) {
            getSetting();
            toast.success(resp?.message);
        } else {
            toast.error(resp?.message);
        }
    }

    const getColorList = async () => {
        loadingShow();
        let resp = await getApi(urlApi.getChatHighlightColorList);
        loadingHide();
        if (resp?.responseCode === 200) {
            setColorList(resp?.data);
        } else {
            setColorList([]);
        }
    }

    const handleDeleteColor = async (colorId) => {
        if (!colorId) return;
        loadingShow();
        let resp = await getApi(urlApi.deleteChatHighlightColor + colorId);
        loadingHide();
        if (resp?.responseCode === 200) {
            toast.success(resp?.message);
            getColorList();
        } else {
            toast.error(resp?.message);
        }
    }

    return (

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Chat Settings</h2>

            {/* Auto Resolve Timer */}
            <div className="bg-white shadow-md rounded-xl p-6 mb-6 border border-gray-200">
                <h3 className="text-lg font-medium text-gray-700 mb-4">⏲️ Auto Resolve Timer</h3>
                <label htmlFor="auto-chat-resolve-time" className="block text-sm text-gray-600 mb-1">
                    Auto Chat Resolve Time (in Minutes)
                </label>
                <div className="flex gap-2 items-center">
                    <input
                        id="auto-chat-resolve-time"
                        type="number"
                        min={0}
                        placeholder="Enter time in minutes"
                        defaultValue={settingData?.chatAutoCloseDuration}
                        className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                    <button
                        onClick={setAutoResolveTimer}
                        className="text-green-600 hover:text-green-700"
                        title="Save"
                    >
                        <IconCircleCheck size={26} />
                    </button>
                    <button
                        onClick={() => {
                            document.getElementById("auto-chat-resolve-time").value = settingData?.chatAutoCloseDuration || "";
                        }}
                        className="text-gray-500 hover:text-gray-700"
                        title="Reset"
                    >
                        <IconXboxX size={26} />
                    </button>
                </div>
            </div>

            {/* AI Mode */}
            <div className="bg-white shadow-md rounded-xl p-6 mb-6 border border-gray-200">
                <h3 className="text-lg font-medium text-gray-700 mb-4">✨ AI Mode</h3>
                <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-600">Enable or disable AI Assistant</span>
                    <div className="flex items-center gap-2">
                        <div
                            className={`relative inline-flex h-6 w-11 rounded-full cursor-pointer transition-colors duration-300 ease-in-out ${settingData?.isAIEnabled == 1 ? 'bg-green-500' : 'bg-gray-300'
                                }`}
                            onClick={toggleAIFeature}
                        >
                            <span
                                className={`inline-block h-6 w-6 transform bg-white rounded-full shadow transition duration-300 ease-in-out ${settingData?.isAIEnabled == 1 ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                            ></span>
                        </div>
                        <span className="text-sm text-gray-600">
                            {settingData?.isAIEnabled == 1 ? 'Enabled' : 'Disabled'}
                        </span>
                    </div>
                </div>
                {settingData?.isAIEnabled == 1 && <div>
                    <label htmlFor="ai-switch-message-count" className="block text-sm text-gray-600 mb-1">
                        After how many message AI switch to agent chat?
                    </label>
                    <div className="flex gap-2 items-center">
                        <input
                            id="ai-switch-message-count"
                            type="number"
                            min={0}
                            placeholder="Enter Number of Messages"
                            defaultValue={settingData?.aiMessageCount}
                            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                        <button
                            onClick={updateAiSwitchMessageCount}
                            className="px-4 py-2 bg-orange-500 text-white rounded-md text-sm hover:bg-orange-600 transition"
                        >
                            Update
                        </button>
                    </div>
                </div>}
            </div>

            {/* Language Selection */}
            <div className="bg-white shadow-md rounded-xl p-6 mb-6 border border-gray-200">
                <div className='mb-4'>
                    <h3 className="text-lg font-medium text-gray-700">🔤 Language Selection</h3>
                    <p className="text-xs italic text-gray-600">*Default is English</p>
                </div>
                <label htmlFor="language-list" className="block text-sm text-gray-600 mb-1">Chat Language</label>
                <select
                    id="language-list"
                    className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                    onChange={(e) => setLang(e.target.value)}
                >
                    <option value={""} disabled>
                        -- Select Language --
                    </option>
                    {languageList?.map((lang) => (
                        <option key={lang?._id} value={lang?._id}>
                            {lang?.language}
                        </option>
                    ))}
                </select>
            </div>

            {/* Ingest PDF */}
            <div className="bg-white shadow-md rounded-xl p-6 mb-6 border border-gray-200">
                <h3 className="text-lg font-medium text-gray-700 mb-4">🗃️ PDF Ingestion</h3>
                <label htmlFor="pdf-selector-input" className="block text-sm text-gray-600 mb-1">
                    Upload PDF (Max. 25MB)
                </label>
                <div className="flex gap-2 items-center">
                    <input
                        type="file"
                        accept="application/pdf"
                        id="pdf-selector-input"
                        className="w-full px-3 py-2 border rounded-md text-sm"
                        onChange={(e) => setPdfFile(e.target.files[0])}
                    />
                    <button
                        onClick={ingestPdf}
                        className="px-4 py-2 bg-orange-500 text-white rounded-md text-sm hover:bg-orange-600 transition"
                    >
                        Ingest
                    </button>
                </div>
            </div>

            {/* Call Feature Toggle */}
            <div className="bg-white shadow-md rounded-xl p-6 mb-6 border border-gray-200">
                <h3 className="text-lg font-medium text-gray-700 mb-4">📞 Call Feature</h3>
                <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Enable or disable voice call feature</span>
                    <div className="flex items-center gap-2">
                        <div
                            className={`relative inline-flex h-6 w-11 rounded-full cursor-pointer transition-colors duration-300 ease-in-out ${settingData?.isCallEnabled == 1 ? 'bg-green-500' : 'bg-gray-300'
                                }`}
                            onClick={toggleCallFeature}
                        >
                            <span
                                className={`inline-block h-6 w-6 transform bg-white rounded-full shadow transition duration-300 ease-in-out ${settingData?.isCallEnabled == 1 ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                            ></span>
                        </div>
                        <span className="text-sm text-gray-600">
                            {settingData?.isCallEnabled == 1 ? 'Enabled' : 'Disabled'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Highlight Conversation */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-xl p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">🔵 Highlight Conversation</h3>
                        <p className='text-xs italic text-gray-600'>Maximum 5 Color Can be added.</p>
                    </div>
                    {colorList?.length < 5 && <AddHighlightColorModal callback={() => getColorList()} />}
                </div>

                {colorList?.length > 0 ? (
                    <div className="flex flex-wrap gap-4">
                        {colorList.map((color) => (
                            <div
                                key={color?._id}
                                className="relative border border-gray-300 rounded-lg px-4 py-2 w-max min-w-32 text-center shadow-sm hover:shadow-md transition"
                            >
                                <ConfirmationModal
                                    onConfirm={() => handleDeleteColor(color?._id)}
                                    confirmationText={`Are you sure you want to delete the color "${color?.name}"?`}
                                    buttonComponent={<button
                                        className="absolute -top-2 -right-2 bg-gray-800 text-white p-1 rounded-full hover:bg-red-600 transition"
                                    >
                                        <IconTrash size={16} />
                                    </button>} />
                                <div
                                    className="w-full h-6 rounded-md mb-2"
                                    style={{ backgroundColor: color?.colorCode }}
                                ></div>
                                <p className="text-sm font-medium text-gray-700 max-line-2">{color?.name}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-gray-500 text-sm text-center py-3">
                        No highlight colors added yet.
                    </div>
                )}
            </div>

        </div>

    )
}

export default ChatSettings